<template>
  <div class="three-column-selector">
    <!-- Loading 狀態 -->
    <div v-if="loading" class="loading-container">
      <Spin size="large"></Spin>
      <div class="loading-text">{{ $t("memberlist['載入學校班級資料中']") }}</div>
    </div>

    <!-- 暫無資料 -->
    <div v-else-if="!schoolData || !periodOptions.length" class="no-data-container">
      <Icon type="ios-information-circle-outline" size="48" color="#c5c8ce" />
      <div class="no-data-text">{{ $t("memberlist['暫無資料']") }}</div>
      <div class="no-data-hint">{{ $t("memberlist['目前沒有可用的學校班級資料']") }}</div>
    </div>

    <!-- 多欄選擇器 -->
    <div v-else class="selector-row">
      <!-- 第一欄：學段 -->
      <div :class="['selector-column', getColumnClass()]">
        <div class="column-header">{{ $t("memberlist['學段']") }}</div>
        <div class="column-list">
          <div
            v-for="period in periodOptions"
            :key="period.periodId"
            :class="['list-item', { 'selected': selectedPeriodId === period.periodId }]"
            @click="selectPeriod(period.periodId)"
          >
            <span class="item-label">{{ period.periodName }}</span>
            <Icon v-if="selectedPeriodId === period.periodId" type="md-checkmark" class="check-icon" />
          </div>

          <div v-if="!periodOptions.length" class="list-item empty-item">
            {{ $t("memberlist['無可用學段']") }}
          </div>
        </div>
      </div>

      <!-- 第二欄：班級類型 -->
      <div :class="['selector-column', getColumnClass()]">
        <div class="column-header">{{ $t("memberlist['班級類型']") }}</div>
        <div class="column-list">
          <div
            v-for="type in classTypes"
            :key="type.value"
            :class="['list-item', {
              'selected': selectedType === type.value,
              'disabled': !selectedPeriodId
            }]"
            @click="selectType(type.value)"
          >
            <span class="item-label">{{ type.label }}</span>
            <Icon v-if="selectedType === type.value" type="md-checkmark" class="check-icon" />
          </div>

          <div v-if="!selectedPeriodId" class="list-item empty-item">
            {{ $t("memberlist['請先選擇學段']") }}
          </div>
        </div>
      </div>

      <!-- 第三欄：年級（只有編制班顯示） -->
      <div v-if="showGradeColumn" :class="['selector-column', getColumnClass()]">
        <div class="column-header">{{ $t("memberlist['年級']") }}</div>
        <div class="column-list">
          <div
            v-for="grade in gradeOptions"
            :key="grade.index"
            :class="['list-item', { 'selected': selectedGradeIndex === grade.index }]"
            @click="selectGrade(grade.index)"
          >
            <span class="item-label">{{ grade.name }}</span>
            <Icon v-if="selectedGradeIndex === grade.index" type="md-checkmark" class="check-icon" />
          </div>

          <div v-if="!gradeOptions.length" class="list-item empty-item">
            {{ $t("memberlist['無可用年級']") }}
          </div>
        </div>
      </div>

      <!-- 第四欄：班級 -->
      <div :class="['selector-column', getColumnClass()]">
        <div class="column-header">{{ $t("memberlist['班級']") }}</div>
        <div class="column-list">
          <!-- 如果是編制班且未選年級，顯示提示 -->
          <div v-if="showGradeColumn && selectedGradeIndex === null && selectedType" class="list-item empty-item">
            {{ $t("memberlist['請先選擇年級']") }}
          </div>

          <!-- 如果是選課班或已選年級，顯示班級列表 -->
          <template v-else-if="selectedType">
            <div
              v-for="group in filteredGroups"
              :key="group.id"
              :class="['list-item', {
                'selected': selectedGroupId === group.id
              }]"
              @click="selectGroup(group)"
            >
              <div class="item-content">
                <div class="item-label">{{ group.name }}</div>
                <div class="item-caption">{{ group._periodName }} · {{ group.scount }} {{ $t("memberlist['人']") }}</div>
              </div>
              <Icon v-if="selectedGroupId === group.id" type="md-checkmark" class="check-icon" />
            </div>

            <div v-if="!filteredGroups.length" class="list-item empty-item">
              {{ $t("memberlist['無符合的班級']") }}
            </div>
          </template>

          <!-- 未選班級類型 -->
          <div v-if="!selectedType" class="list-item empty-item">
            {{ $t("memberlist['請先選擇班級類型']") }}
          </div>
        </div>
      </div>
    </div>

    <!-- 已選擇的班級預覽 -->
    <div v-if="selectedGroup" class="selected-preview">
      <div class="preview-card">
        <div class="preview-title">{{ $t("memberlist['已選擇']") }}</div>
        <div class="preview-content">
          <div class="preview-info">
            <div class="class-name-row">
              {{ selectedGroup.name }}
              <span class="type-tag">{{ selectedTypeLabel }}</span>
            </div>
            <div class="class-detail">
              <template v-if="selectedGradeName">
                {{ selectedGroup._periodName }} · {{ selectedGradeName }} · {{ selectedGroup.scount }} {{ $t("memberlist['位學生']") }}
              </template>
              <template v-else>
                {{ selectedGroup._periodName }} · {{ selectedGroup.scount }} {{ $t("memberlist['位學生']") }}
              </template>
            </div>
          </div>
          <Button type="primary" ghost size="small" @click="showPreview = true">
            {{ $t("memberlist['預覽名單']") }}
          </Button>
        </div>
      </div>
    </div>

    <!-- 學生名單預覽對話框 -->
    <transition name="modal-fade">
      <div v-if="showPreview" class="custom-modal-overlay" @click="closePreview">
        <div class="custom-modal" @click.stop>
          <div class="custom-modal-header">
            <div class="custom-modal-title">
              {{ $t("memberlist['學生名單']") }}
              <span v-if="selectedGroup && selectedGroup.name"> - {{ selectedGroup.name }}</span>
            </div>
            <div class="custom-modal-close" @click="closePreview">
              <Icon type="ios-close" size="24" />
            </div>
          </div>

          <div class="custom-modal-body">
            <div class="student-list">
              <template v-if="selectedGroup && selectedGroup.members && selectedGroup.members.length > 0">
                <div
                  v-for="(member, index) in selectedGroup.members"
                  :key="member.id"
                  class="student-item"
                >
                  <div class="student-number">{{ index + 1 }}</div>
                  <div class="student-info">
                    <div class="student-name">{{ member.name }}</div>
                    <div class="student-no">{{ member.no || '-' }}</div>
                  </div>
                </div>
              </template>

              <div v-else class="no-students">
                <Icon type="ios-information-circle-outline" size="36" color="#c5c8ce" />
                <div class="no-students-text">{{ $t("memberlist['暫無數據']") }}</div>
              </div>
            </div>
          </div>

          <div class="custom-modal-footer">
            <button class="modal-btn modal-btn-primary" @click="closePreview">
              {{ $t("memberlist['關閉']") }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ThreeColumnSelector',

  props: {
    schoolData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedPeriodId: null,  // 新增：選中的學段 ID
      selectedType: null,      // 'class' | 'teach'
      selectedGradeIndex: null, // 0, 1, 2...
      selectedGroupId: null,
      selectedGroup: null,
      showPreview: false,

      classTypes: [
        { label: this.$t("memberlist['編制班']"), value: 'class' },
        { label: this.$t("memberlist['選課班']"), value: 'teach' }
      ]
    }
  },

  computed: {
    // 所有學段選項
    periodOptions() {
      if (!this.schoolData || !this.schoolData.data) return []
      return this.schoolData.data.map(period => ({
        periodId: period.periodId,
        periodName: period.periodName
      }))
    },

    // 當前選擇的學段資料
    currentPeriod() {
      if (!this.selectedPeriodId || !this.schoolData || !this.schoolData.data) return null
      return this.schoolData.data.find(p => p.periodId === this.selectedPeriodId)
    },

    // 當前學段的所有班級
    currentPeriodGroups() {
      if (!this.currentPeriod || !this.currentPeriod.groups) return []

      // 為每個班級加入學段資訊
      return this.currentPeriod.groups.map(g => ({
        ...g,
        _periodId: this.currentPeriod.periodId,
        _periodName: this.currentPeriod.periodName,
        _periodGrades: this.currentPeriod.grades,
        _periodYears: this.currentPeriod.years
      }))
    },

    // 是否顯示年級欄（只有編制班才顯示）
    showGradeColumn() {
      return this.selectedType === 'class'
    },

    // 年級選項（用 years 陣列對應 grades 陣列，只顯示有班級的年級）
    gradeOptions() {
      if (!this.selectedType || this.selectedType !== 'class' || !this.currentPeriod) return []
      const grades = this.currentPeriod.grades || []
      const years = this.currentPeriod.years || []
      const classGroups = this.currentPeriodGroups.filter(g => g.type === 'class')
      return grades.map((gradeName, i) => ({
        name: years[i] ? `${gradeName}(${years[i]}${this.$t("memberlist['年級後綴']")})` : gradeName,
        year: years[i] || null,
        index: i
      })).filter(grade => classGroups.some(g => g.year === grade.year))
    },

    // 過濾後的班級列表
    filteredGroups() {
      if (!this.selectedType || !this.currentPeriod) return []

      let filtered = this.currentPeriodGroups.filter(g => {
        const matchType = (this.selectedType === 'class' && g.type === 'class') ||
                          (this.selectedType === 'teach' && g.type === 'teach')
        return matchType
      })

      // 如果是編制班且選擇了年級，用 year 欄位過濾
      if (this.selectedType === 'class' && this.selectedGradeIndex !== null) {
        const selectedGrade = this.gradeOptions.find(g => g.index === this.selectedGradeIndex)
        if (selectedGrade && selectedGrade.year !== null) {
          filtered = filtered.filter(g => g.year === selectedGrade.year)
        }
      }

      return filtered
    },

    selectedTypeLabel() {
      const type = this.classTypes.find(t => t.value === this.selectedType)
      return type ? type.label : ''
    },

    selectedGradeName() {
      if (this.selectedGradeIndex === null) return ''
      const grade = this.gradeOptions.find(g => g.index === this.selectedGradeIndex)
      return grade ? grade.name : ''
    }
  },

  methods: {
    // 計算欄位寬度 class
    getColumnClass() {
      // 學段 + 班級類型 + 年級（可選） + 班級 = 3 或 4 欄
      const totalColumns = this.showGradeColumn ? 4 : 3
      return `col-${totalColumns}`
    },

    /**
     * 從班級名稱推算年級
     * @param {string} className - 班級名稱（如 "901", "4年1班"）
     * @param {array} periodGrades - 學段的年級列表
     * @returns {object|null} - { name: '九年級', index: 2 } 或 null
     */
    getGradeFromClassName(className, periodGrades) {
      if (!className || !periodGrades) return null

      // 提取班級名稱中的第一個數字
      const match = className.match(/^(\d)/)
      if (!match) return null

      const gradeNum = parseInt(match[1])

      // 建立數字到年級的映射
      const gradeMap = {
        1: '一年級', 2: '二年級', 3: '三年級',
        4: '四年級', 5: '五年級', 6: '六年級',
        7: '七年級', 8: '八年級', 9: '九年級'
      }

      const gradeName = gradeMap[gradeNum]
      if (!gradeName) return null

      // 在學段的年級列表中查找索引
      const index = periodGrades.indexOf(gradeName)
      if (index === -1) return null

      return {
        name: gradeName,
        index: index
      }
    },

    selectPeriod(periodId) {
      this.selectedPeriodId = periodId
      // 重置後續選擇
      this.selectedType = null
      this.selectedGradeIndex = null
      this.selectedGroupId = null
      this.selectedGroup = null
    },

    selectType(typeValue) {
      // 如果沒有選擇學段，不允許選擇類型
      if (!this.selectedPeriodId) {
        return
      }

      this.selectedType = typeValue
      // 重置後續選擇
      this.selectedGradeIndex = null
      this.selectedGroupId = null
      this.selectedGroup = null
    },

    selectGrade(gradeIndex) {
      this.selectedGradeIndex = gradeIndex
      // 重置班級選擇
      this.selectedGroupId = null
      this.selectedGroup = null
    },

    selectGroup(group) {
      // 如果是禁用狀態，不處理點擊
      if (this.showGradeColumn && this.selectedGradeIndex === null) {
        return
      }

      this.selectedGroupId = group.id
      this.selectedGroup = group

      // 如果是編制班，取得年級資訊（用 year 欄位比對）
      let gradeInfo = null
      if (this.selectedType === 'class') {
        const years = group._periodYears || []
        const grades = group._periodGrades || []
        const idx = years.indexOf(group.year)
        if (idx !== -1) {
          gradeInfo = { index: idx, name: grades[idx] }
        }
      }

      // 觸發選擇事件
      this.$emit('select', {
        type: this.selectedType,
        gradeIndex: gradeInfo ? gradeInfo.index : null,
        gradeName: gradeInfo ? gradeInfo.name : null,
        periodName: group._periodName,
        group: group
      })
    },

    closePreview() {
      this.showPreview = false
    },

    handleEscKey(event) {
      if (event.key === 'Escape' && this.showPreview) {
        this.closePreview()
      }
    }
  },

  mounted() {
    window.addEventListener('keydown', this.handleEscKey)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleEscKey)
  }
}
</script>

<style lang="less" scoped>
.three-column-selector {
  padding: 16px;

  // Loading 狀態
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;

    .loading-text {
      margin-top: 16px;
      color: #999;
      font-size: 14px;
    }
  }

  // 暫無資料
  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;

    .no-data-text {
      margin-top: 16px;
      color: #666;
      font-size: 16px;
      font-weight: 600;
    }

    .no-data-hint {
      margin-top: 8px;
      color: #999;
      font-size: 13px;
    }
  }

  // 多欄選擇器
  .selector-row {
    display: flex;
    gap: 12px;
  }

  .selector-column {
    flex: 1;

    &.col-3 {
      flex: 0 0 calc(33.333% - 8px);
    }

    &.col-4 {
      flex: 0 0 calc(25% - 9px);
    }
  }

  .column-header {
    font-weight: 600;
    font-size: 14px;
    color: #ffffff;
    margin-bottom: 8px;
    padding: 8px 12px;
    background: #909399;
    border-radius: 4px;
  }

  .column-list {
    border: 1px solid #dcdee2;
    border-radius: 4px;
    max-height: 300px;
    overflow-y: auto;
    background-color: #ffffff;

    // 滾動條樣式
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #dcdee2;
      border-radius: 3px;

      &:hover {
        background-color: #c8c9cc;
      }
    }
  }

  .list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    &:hover:not(.selected):not(.disabled):not(.empty-item) {
      background-color: #f5f5f5;
    }

    &.selected {
      background-color: #e3f2fd;
      border-left: 3px solid #2d8cf0;
      padding-left: 9px;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &.empty-item {
      justify-content: center;
      color: #999;
      cursor: default;
      font-size: 13px;
    }

    .item-content {
      flex: 1;
    }

    .item-label {
      font-size: 14px;
      color: #333;
    }

    .item-caption {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
    }

    .check-icon {
      color: #2d8cf0;
      font-size: 18px;
      flex-shrink: 0;
    }
  }

  // 已選擇預覽
  .selected-preview {
    margin-top: 16px;
  }

  .preview-card {
    background-color: #f8fcff;
    border: 1px solid #d9ecff;
    border-radius: 4px;
    padding: 16px;
  }

  .preview-title {
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
    font-weight: 600;
  }

  .preview-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .preview-info {
    flex: 1;
  }

  .class-name-row {
    font-size: 15px;
    color: #333;
    font-weight: 500;
    margin-bottom: 6px;

    .type-tag {
      display: inline-block;
      background-color: #2d8cf0;
      color: white;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 3px;
      margin-left: 8px;
      font-weight: normal;
    }
  }

  .class-detail {
    font-size: 13px;
    color: #666;
  }

  // 自定義 Modal 樣式
  .custom-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .custom-modal {
    background: #ffffff;
    border-radius: 8px;
    width: 650px;
    max-width: 90vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .custom-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e8eaec;
  }

  .custom-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .custom-modal-close {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  .custom-modal-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .custom-modal-footer {
    padding: 12px 20px;
    border-top: 1px solid #e8eaec;
    text-align: right;
  }

  .modal-btn {
    padding: 8px 20px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    outline: none;

    &:hover {
      opacity: 0.85;
    }
  }

  .modal-btn-primary {
    background-color: #2d8cf0;
    color: #ffffff;

    &:hover {
      background-color: #2b85e4;
    }
  }

  // Modal 過渡動畫
  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 0.3s;
  }

  .modal-fade-enter,
  .modal-fade-leave-to {
    opacity: 0;
  }

  .modal-fade-enter-active .custom-modal,
  .modal-fade-leave-active .custom-modal {
    transition: transform 0.3s;
  }

  .modal-fade-enter .custom-modal,
  .modal-fade-leave-to .custom-modal {
    transform: scale(0.9);
  }

  .student-list {
    flex: 1;
    overflow-y: auto;
    padding: 16px 0 16px 20px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #dcdee2;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
      margin: 0;
    }
  }

  .no-students {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 14px 60px 0;

    .no-students-text {
      margin-top: 12px;
      color: #999;
      font-size: 14px;
    }
  }

  .student-item {
    display: flex;
    align-items: center;
    padding: 12px 14px 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    .student-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: #f5f5f5;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      flex-shrink: 0;
      margin-right: 12px;
    }

    .student-info {
      flex: 1;
    }

    .student-name {
      font-size: 14px;
      color: #333;
      margin-bottom: 4px;
    }

    .student-no {
      font-size: 12px;
      color: #999;
    }
  }
}
</style>
